import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import cx from 'classnames';
import * as yup from 'yup';

import { didPropChange } from 'utils/props';
import { formatNumber, formatMoney } from 'utils/utils';
import { Urls, ModalTypes, Email, ConfirmType } from 'constants/constants';

import { selectCurrentUser } from 'store/AppState';
import { selectUsers } from 'store/models/User';
import Account, { selectAccountSettings, selectAccountBots, selectCurrentAccount, getAllTags, getTag, isFreeAccount } from 'store/models/Account';
import Overlay, { selectOverlays } from 'store/models/Overlay';
import RouteState, { pushTo } from 'store/RouteState';
import UIState from 'store/UIState';


import { ContentCenter, Header, Title, Breadcrumbs } from 'components/Content';
import Card, { CardHead, CardTitle, CardInputItem, ItemLabel, ItemInput, CardItem, ItemActions } from 'components/Card';
import TextInput from 'components/forms/TextInput';
import * as Icons from 'components/Svg';
import Button, { ButtonList, SaveButton, SmallButton, AddButton, CancelButton, DeleteIconButton } from 'components/Button';
import { TagList, AccountGroupTag } from 'components/Tag';
import { withForm } from 'components/forms/Form';
import Label from 'components/forms/Label';
import ActionBar from 'components/ActionBar';
import UserSelect from 'components/UserSelect';




// ======================
// FORM
// ====================== 
const SCHEMA = {
  cpm: {
    default: 0.50,
    validate: yup.number().min(0.10).required(),
    transformOut: v => parseFloat(v),
    transformIn: v => formatMoney(v, 3),
  },
  cpc: {
    default: 0.30,
    validate: yup.number().min(0.01).required(),
    transformOut: v => parseFloat(v),
    transformIn: v => formatMoney(v, 3),
  },
  botFrequency: {
    default: 5,
    validate: yup.number().min(1).required(),
    transformOut: v => v |> parseInt |> # * 60000,
    transformIn: v => v |> _.toNumber |> # / 60000 |> _.round(#, 0),
  }
}


// ======================
// STORE
// ====================== 
const selectors = createSelector(
  selectCurrentUser,
  selectCurrentAccount,
  selectAccountSettings,
  (s) => selectAccountBots(s) |> selectUsers(s, #),
  selectOverlays,
  (user, account, settings, botUsers, overlays) => {
    overlays = _.values(overlays);
    return { 
      user, 
      account,
      settings,
      botUsers,
      overlays,
    }
  })







// ======================
// COMPONENT
// ====================== 

const AccountSettings = ({ values, errors, edits, setDefaults, setSchema, setErrors, validate, onInputChange}) => {
  const {user, account, settings, botUsers, overlays } = useSelector(selectors);
  const [saving, setSaving] = useState(false);
  const [tagNameValue, setTagNameValue] = useState();
  const [showCreateTag, setShowCreateTag] = useState(false);
  const [overlayValues, setOverlayValues] = useState({});
  let [hasOverlayEdits, setHasOverlayEdits] = useState(false);
  const dispatch = useDispatch();
  const tags = getAllTags(account);
  const hasTags = !_.isEmpty(tags);
  const isFree = isFreeAccount(account);
  const hasEdits = _.some(edits, v => v === true) || hasOverlayEdits;
  
  const fetchData = async () => {
    return Promise.all([
      dispatch(Account.actions.fetchAccount()),
      dispatch(Account.actions.fetchChatBots()),
      dispatch(Overlay.actions.fetchOverlaysByAccount()),
    ])
  };

  const addTwitchBotAccount = () => dispatch(UIState.actions.withToasts(
    Account.actions.addTwitchBotAccount(),
    "Twitch account has been added",
    "Twitch account failed to add"
  ));

  const removeBotAccount = (userId) => dispatch(UIState.actions.withToasts(
    Account.actions.removeBotAccount(userId),
    "This Twitch account has been removed"
  ));

  useEffect(() =>{
    (async () => {
      await fetchData();
      await setSchema(SCHEMA);
      if(settings)
        setDefaults(_.pick(settings, _.keys(SCHEMA)))
    })();
  }, [])

  useEffect(() => {
    setDefaults(_.pick(settings, _.keys(SCHEMA)))
  }, [settings])

  useEffect(() => {
    let values = _.reduce(overlays, (acc, overlay) => {
      return _.set(acc, overlay._id, overlay.name)
    }, {});
    setOverlayValues(values);
  }, [overlays])
  
  
  const onSuccess = () => { 
    dispatch(UIState.actions.showSuccessToast("Settings Updated")) 
  };
  const onBotChange = userId => dispatch(Account.actions.updateDefaultBotAccount(userId));
  const onBotAdd = () => dispatch(UIState.actions.showModal(ModalTypes.CONFIRM, { 
    title: "Sign in to your Twitch Account",
    subtitle: "Please sign in to the Twitch account you want to use for your BOT in another tab. Then hit \"Continue\" below.",
    confirmText: "Continue",
    onConfirm: async () => {
      await addTwitchBotAccount();
      await fetchData();
    },
    onCancel: () => {}
  }));
  const onBotRemove = (userId) => removeBotAccount(userId);
  const onSlideshowSettings = () => pushTo(Urls.SLIDESHOW);
  const onOverlayNameChange = (value, id) => { 
    setHasOverlayEdits(true);
    setOverlayValues({...overlayValues, [id]: value});
  }
  const onCreateOverlay = () => {
    dispatch(UIState.actions.showModal(ModalTypes.FORM, {
      title: 'Create New Slideshow Overlay',
      confirmType: ConfirmType.SAVE,
      onSave: async values => {
        try {
          await dispatch(Overlay.actions.createOverlay(values))
        } catch (err) {
          dispatch(UIState.actions.showErrorToast(err.message));
        }
      },
      inputs: {
        'name': {
          component: TextInput,
          props: {
            label: "Name",
            value: '',
            focused: true,
          },
        },
      }
    }))
  };
  const onDeleteOverlay = (overlayId) => { 
    dispatch(UIState.actions.showModal(ModalTypes.CONFIRM, {
      title: "Delete This Overlay?",
      subtitle: "NOTE: Any campaigns targeting this overlay will be PAUSED. You can reassign them and unpause at any time.",
      confirmText: "Yes, Delete",
      confirmType: ConfirmType.DELETE,
      onConfirm: async () => {
        try {
          await dispatch(Overlay.actions.deleteOverlay(overlayId));
        } catch (err) {
          dispatch(UIState.actions.showErrorToast(err.message));
        }
      },
      onCancel: () => {}
    }))
  }
  const onShowCreateTag = () => setShowCreateTag(!showCreateTag);
  const onTagInputChange = value => setTagNameValue(value);
  const onDeleteChannelTag = tagId => dispatch(Account.actions.deleteTag(tagId));
  const onEditChannelTagSuccess = (tagId, values) => dispatch(Account.actions.updateTag(tagId, values.tagName));
  const onEditChannelTag = tagId => {
    let tag = getTag(account, tagId);
    dispatch(UIState.actions.showModal(ModalTypes.FORM, {
      title: 'Edit Tag',
      onSave: onEditChannelTagSuccess(tagId, ?, ?, ?),
      inputs: {
        'tagName': {
          component: TextInput,
          props: {
            label: "Tag Name",
            value: tag.name,
          },
        },
      }
    }))
  };
  const onCreateChannelTag = async () => { 
    try {
      await dispatch(Account.actions.createTag(tagNameValue));
      setShowCreateTag(false);
      setTagNameValue();
    } catch (err) {
      //NOOP
    }
  };
  const onSave = async () => {
    let values = await validate();
    if(!values)
      return;

    setSaving(true);
    try {
      await dispatch(Account.actions.updateSettings(values));
      await Promise.all(_.map(overlays, overlay => {
        let name = overlayValues[overlay._id];
        return dispatch(Overlay.actions.updateOverlay(overlay._id, { name }));
      }));
      onSuccess();
    } catch (err) {
      setErrors({ server: err.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <ContentCenter className="account-settings">
      <Header>
        <Title>Account Settings</Title>
      </Header>

      <Card>
        <CardHead>
          <CardTitle>Account Status</CardTitle>
        </CardHead>
        <CardInputItem>
          <ItemLabel>
            <Label>Type</Label>
          </ItemLabel>
          <ItemInput className="account-status">
            { isFree 
              ? <>
                  <strong>Free Account</strong>
                  <div className="status-helper">
                    You have a free account, which limits you to one channel. To add more channels, please email <strong>{Email.SALES}</strong> and upgrade to a premium account.
                  </div>
                </>
              : <>
                  <strong>Premium Account</strong>
                  <div className="status-helper">All account features are unlocked</div>
                </>}
          </ItemInput>
        </CardInputItem>
      </Card>

      <Card className="account-overlays">
        <CardHead>
          <CardTitle>Slideshow Overlays</CardTitle>
        </CardHead>
        { _.map(overlays, (overlay, idx) => (
        <CardInputItem key={overlay._id}>
          <ItemLabel>
            <Label>Overlay {idx + 1}</Label>
          </ItemLabel>
          <ItemInput>
            <TextInput
              name={overlay._id}
              value={overlayValues[overlay._id]}
              label="Name"
              onChange={onOverlayNameChange}
              helper={idx === 0 ? "The name you'll see in your reports or when assigning a campaign to this overlay." : ""}>
                { idx != 0 ?
                    <DeleteIconButton onClick={() => onDeleteOverlay(overlay._id)} /> : 
                    null }
              </TextInput>
              
          </ItemInput>
        </CardInputItem>
        ))}
        <CardInputItem>
          <ItemLabel>
            <Label></Label>
          </ItemLabel>
          <ItemInput>
            <SmallButton onClick={onCreateOverlay}><Icons.EditIcon />  Create Additional Overlay</SmallButton>
            <p className="helper-text">Create a new overlay. Use this if you need to show two different slideshows on a stream.</p>
          </ItemInput>
        </CardInputItem>
      </Card>

      <Card>
        <CardHead>
          <CardTitle>Slideshow Settings</CardTitle>
        </CardHead>
        <CardInputItem>
          <ItemLabel>
            <Label>Default CPM</Label>
          </ItemLabel>
          <ItemInput>
            <TextInput
              name="cpm"
              value={values.cpm}
              onChange={onInputChange}
              helper={"The CPM used to calculate your Media Value. (Cost Per 1000 Impressions)"}
              prefix="$" />
          </ItemInput>
        </CardInputItem>
        <CardInputItem>
          <ItemLabel>
            <Label>Visual Design</Label>
          </ItemLabel>
          <ItemInput>
            <SmallButton onClick={onSlideshowSettings}><Icons.EditIcon />  Slideshow Editor</SmallButton>
            <p className='helper-text'>Change the look of your slideshows across all slides and channels.</p>
          </ItemInput>
        </CardInputItem>
      </Card>


      <Card>
        <CardHead>
          <CardTitle>Channel Group Tags</CardTitle>
        </CardHead>
        <CardItem>
          <p className="tag-desc">Add tags to your channels to aggregated data and reports. Perfect for grouping teams, regions, or types of streamers.</p>
          { hasTags &&
          <TagList>
            { _.map(tags, tag => <AccountGroupTag key={tag.tagId} tag={tag} onDelete={onDeleteChannelTag} onEdit={onEditChannelTag} />)}
          </TagList>}
          { !showCreateTag && 
          <AddButton size="s" onClick={onShowCreateTag}>Create Tag</AddButton>}
        </CardItem>
        { showCreateTag && 
        <CardItem className="add-tag">
          <TextInput className="tag-name" value={tagNameValue} onChange={onTagInputChange} autoFocus={true}/>
          <ButtonList size="s">
            <Button size="s" onClick={onCreateChannelTag}>Save</Button>
            <CancelButton size="s" onClick={onShowCreateTag}>Cancel</CancelButton>
          </ButtonList>
        </CardItem>}
      </Card>

      <Card>
        <CardHead>
          <CardTitle>Chat Bot Settings</CardTitle>
        </CardHead>
        <CardInputItem>
          <ItemLabel>
            <Label>Default CPC</Label>
          </ItemLabel>
          <ItemInput>
            <TextInput
              name="cpc"
              value={values.cpc}
              onChange={onInputChange}
              helper={"The CPC used to calculate your Media Value for links. (Cost Per Click)"}
              prefix="$" />
          </ItemInput>
        </CardInputItem>
        <CardInputItem>
          <ItemLabel>
            <Label>Default Frequency</Label>
          </ItemLabel>
          <ItemInput>
            <TextInput
              name="botFrequency"
              value={values.botFrequency}
              onChange={onInputChange}
              helper={"The cooldown period between posting links in chat. One of your campaign links will be posted every X minutes. This can be overriden on a campaign and on a channel."} />
          </ItemInput>
        </CardInputItem>
        <CardInputItem>
          <ItemLabel>
            <Label>Default Chat Bot</Label>
          </ItemLabel>
          <ItemInput className="bot-account">
            { settings?.botUserId && _.size(botUsers) > 0
              ? <>
                  <div className="bot-account-selector">
                    <UserSelect 
                      users={_.values(botUsers)} 
                      size="s" 
                      selected={settings?.botUserId}
                      showDelete={true} 
                      onChange={onBotChange}
                      onDelete={onBotRemove} />
                  </div>
                  <div className="bot-helper">
                    This Twitch account will be the chat bot used to post links in your channels. Your streamers must add this account as a /mod for the messages to appear.
                  </div>
                </>
              : <div className="bot-helper">
                  Connect a Twitch account to HappyStreams, and we'll use it as your chat bot across all your channels. You can also add multiple Twitch accounts and change them at any time.
                </div>}
            <SmallButton className="add-bot" onClick={onBotAdd}>Add New Chat Bot</SmallButton>
          </ItemInput>
        </CardInputItem>
      </Card>

      <ActionBar type="fixed">
        <SaveButton onClick={onSave} loading={saving} disabled={!hasEdits} />
      </ActionBar>
    </ContentCenter>);
}

export default AccountSettings 
  |> withForm;



